import React, { createContext, useState, useEffect, ReactNode } from 'react';

type Language = 'pl' | 'en';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
}

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('pl');

  useEffect(() => {
    // First try to get the saved language from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    
    if (savedLanguage) {
      // If there's a saved language preference, use it
      setLanguage(savedLanguage);
      document.documentElement.setAttribute('data-language', savedLanguage);
    } else {
      // If no saved preference, detect browser language
      const browserLanguage = navigator.language.toLowerCase();
      const detectedLanguage = browserLanguage.startsWith('pl') ? 'pl' : 'en';
      
      setLanguage(detectedLanguage);
      document.documentElement.setAttribute('data-language', detectedLanguage);
      localStorage.setItem('language', detectedLanguage);
    }
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'pl' ? 'en' : 'pl';
    setLanguage(newLanguage);
    document.documentElement.setAttribute('data-language', newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}; 